import { type ReactNode, useState } from 'react';
import { ShieldCheck } from 'react-bootstrap-icons';

import { type BillingPlanKind, DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';
import { StripeLogoIcon } from '@repo/ui/assets/icons/paymentIcons/stripe-logo-icon';
import { LinearProgressBar } from '@repo/ui/components/elements/LinearProgressBar/LinearProgressBar';
import { useFeatureFlag } from '@repo/ui/hooks/use-feature-flag';

import { formatBillingDate } from './billing-format';
import {
  describeFinalPaymentDue,
  describeInstallmentProgress,
  describeNextPaymentDue,
  describePlanKind,
  describePlanTotal,
  formatBillingPlanAmount,
  isInstallmentPlanKind,
  resolveFounderDiscountPercent,
} from './billing-plan-format';
import { RefundPolicyNote } from './refund/refund-policy-link';
import { RefundRequestDialog, type RefundSubject } from './refund/refund-request-dialog';

export interface PaymentSummaryPlan {
  kind: BillingPlanKind;
  productTitle: string;
  planTotal: { amountMinor: number; currency: string };
  totalPaid: { amountMinor: number; currency: string };
  isComplete: boolean;
  paidAt?: string | null;
  installmentCount?: number | null;
  installmentsPaid?: number | null;
  remainingBalance?: { amountMinor: number; currency: string } | null;
  nextPaymentAmount?: { amountMinor: number; currency: string } | null;
  nextPaymentDueAt?: string | null;
  finalPaymentDueAt?: string | null;
}

interface SummaryFactProps {
  heading: string;
  value: string;
  supportingText?: string;
  dataTestId?: string;
  className?: string;
}

/**
 * A rule between two facts that actually shows.
 *
 * SDS gives a vertical separator `height: 100%`, which in a row whose height is
 * set by its content resolves to nothing — the rule was there and invisible.
 * Stretching it instead takes the height of the tallest fact beside it.
 */
function FactRule() {
  return <SDSSeparator className="FactRule" orientation="vertical" />;
}

function SummaryFact({ heading, value, supportingText, dataTestId, className }: SummaryFactProps) {
  return (
    <SDSFlexbox
      className={className}
      direction="column"
      gap="text-small"
      alignItems="flex-start"
      dataTestId={dataTestId}
    >
      {/* Body/Large over an H4 figure, with the supporting line back at
          Body/Large — both plan cards set their facts this way. */}
      <SDSText type="body-large" text={heading} color="text-secondary" />
      <SDSText type="h4" text={value} />
      {supportingText ? <SDSText type="body-large" text={supportingText} color="text-secondary" /> : null}
    </SDSFlexbox>
  );
}

/**
 * The shield, a line of muted copy and the Stripe mark — the same element the
 * frames put under the plan's actions and under the saved cards, with only the
 * words changing.
 *
 * The shield is the one the pricing page already draws for the same promise,
 * rather than a second copy of the Figma asset.
 */
export function StripeNotice({
  text,
  className,
  dataTestId,
}: {
  text: string;
  className?: string;
  dataTestId?: string;
}) {
  return (
    <SDSFlexbox className={className} direction="row" gap="button-small" alignItems="center" dataTestId={dataTestId}>
      <ShieldCheck className="StripeNotice__icon" size={16} aria-hidden="true" />
      {/* "Buttons/Medium" in text/muted, as the frame sets it — 12/15 at
          weight 500. body-small is the same size at 400 and reads lighter. */}
      <SDSText type="button-medium" text={text} color="muted" />
      <StripeLogoIcon className="StripeNotice__logo" />
    </SDSFlexbox>
  );
}

function StripeFooter({ className }: { className?: string }) {
  return (
    <StripeNotice
      className={className}
      text="Payment securely processed by"
      dataTestId={DataTestIds.BILLING.STRIPE_NOTICE}
    />
  );
}

/**
 * The way into the refund flow.
 *
 * The eligibility rules are not consulted here — the dialog reads them when it
 * opens. Hiding the entry point for an ineligible purchase would leave someone
 * who wants their money back with nothing to click and no explanation, which
 * is the one outcome the ineligible states exist to avoid.
 */
function RequestRefundButton({ className, subject }: { className?: string; subject: RefundSubject }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isRefundRequestEnabled = useFeatureFlag('billing_refund_request');

  // Dark until the refund policy exists to link to and the completion metric
  // behind the eligibility cap is real.
  if (!isRefundRequestEnabled) {
    return null;
  }

  return (
    <>
      <SDSButton
        className={className}
        text="Request refund"
        mode="ghost"
        size="medium"
        onClick={() => {
          setIsDialogOpen(true);
        }}
        dataTestId={DataTestIds.BILLING.REQUEST_REFUND}
      />
      {/* Mounted only while open, so each visit re-reads eligibility and
          starts with empty reason fields rather than whatever was typed and
          abandoned last time. */}
      {isDialogOpen ? (
        <RefundRequestDialog
          isOpen
          subject={subject}
          onClose={() => {
            setIsDialogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A plan card's actions, laid out the way every frame lays them out.
 *
 * Two rows above phone width: the plan's own actions on the left with the
 * refund link pushed to the far edge, then the Stripe line with the policy
 * note opposite it. On a phone the actions go full width and the refund link
 * drops beneath the Stripe line — a move across rows, which is why the four
 * pieces are siblings in one grid rather than two flex rows.
 */
export function PackageActions({ children, refundSubject }: { children: ReactNode; refundSubject?: RefundSubject }) {
  return (
    <div className="PackageActions" data-testid={DataTestIds.BILLING.PACKAGE_ACTIONS}>
      <SDSFlexbox className="PackageActions__primary" direction="row" gap="button-medium" wrap="wrap">
        {children}
      </SDSFlexbox>
      {/* A refunded plan keeps the row but loses the link: there is nothing
          left to refund, and the refunded frame draws no link there. */}
      {refundSubject ? <RequestRefundButton className="PackageActions__refund" subject={refundSubject} /> : null}
      <StripeFooter className="PackageActions__stripe" />
      <RefundPolicyNote
        layout="stacked"
        className="PackageActions__policy"
        dataTestId={DataTestIds.BILLING.REFUND_POLICY_NOTE}
        linkDataTestId={DataTestIds.BILLING.REFUND_POLICY_NOTE_LINK}
      />
    </div>
  );
}

/**
 * The plan can only be settled in full or bought as a subscription. A
 * discount line renders only once that fork is resolved, so both branches
 * share it here rather than each repeating the same static copy.
 */
/**
 * Shown only to a plan that actually got the founding price.
 *
 * Telling someone who paid list price that a discount was applied is worse
 * than saying nothing, so a plan the constants do not recognise renders no
 * line at all.
 */
function DiscountLine({ plan }: { plan: PaymentSummaryPlan }) {
  const discountPercent = resolveFounderDiscountPercent(plan.kind, plan.planTotal);
  if (discountPercent === undefined) {
    return null;
  }

  return (
    // The frame's words: the line says the discount applies, not how big it
    // was. The percentage still decides whether the line appears at all.
    <SDSText
      type="body-large"
      text="Founder’s discount applied"
      color="primary"
      dataTestId={DataTestIds.BILLING.SUMMARY_DISCOUNT}
    />
  );
}

// A paid-in-full purchase has nothing left to collect, so the portal has
// nothing to offer it either: the receipt is the whole action.
function OneTimeSummary({
  plan,
  isReceiptLoading,
  onViewReceipt,
}: {
  plan: PaymentSummaryPlan;
  isReceiptLoading: boolean;
  onViewReceipt(): void;
}) {
  return (
    <SDSFlexbox direction="column" gap="no-design-24" alignItems="flex-start" fullWidth>
      <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start">
        <SDSText type="h4" text={describePlanKind(plan.kind)} dataTestId={DataTestIds.BILLING.SUMMARY_PLAN_KIND} />
        <SDSText type="body-large" text="Paid in full" color="text-secondary" />
        <DiscountLine plan={plan} />
      </SDSFlexbox>

      <SDSSeparator />

      {/* Two equal columns with a rule between them, 24px apart. */}
      <SDSFlexbox direction="row" gap="no-design-24" alignItems="stretch" fullWidth>
        <SummaryFact
          className="SummaryFact--fill"
          heading="Amount paid"
          value={formatBillingPlanAmount(plan.totalPaid)}
          dataTestId={DataTestIds.BILLING.SUMMARY_AMOUNT_PAID}
        />
        <FactRule />
        <SummaryFact
          className="SummaryFact--fill"
          heading="Payment date"
          value={plan.paidAt ? formatBillingDate(plan.paidAt) : '—'}
          dataTestId={DataTestIds.BILLING.SUMMARY_PAYMENT_DATE}
        />
      </SDSFlexbox>

      <PackageActions refundSubject={{ productTitle: plan.productTitle, planLabel: describePlanKind(plan.kind) }}>
        <SDSButton
          className="PackageActions__receipt"
          text="View receipt"
          mode="primary"
          size="medium"
          isSubmitting={isReceiptLoading}
          onClick={onViewReceipt}
          dataTestId={DataTestIds.BILLING.VIEW_RECEIPT}
        />
      </PackageActions>
    </SDSFlexbox>
  );
}

// No receipt control here: a plan's own card offers the plan, and each
// instalment's receipt belongs to the history row that charged it.
function InstallmentSummary({
  plan,
  isPortalLoading,
  onOpenPortal,
}: {
  plan: PaymentSummaryPlan;
  isPortalLoading: boolean;
  onOpenPortal(): void;
}) {
  const installmentCount = plan.installmentCount ?? 0;
  const installmentsPaid = plan.installmentsPaid ?? 0;
  // Stripe's portal can pay an open invoice, but it cannot prepay a
  // subscription — offering the button once the plan has no next payment
  // would send someone to a page that cannot do what the button says.
  const canPayRemainingBalance = Boolean(plan.nextPaymentDueAt);

  return (
    <SDSFlexbox direction="column" gap="no-design-24" alignItems="flex-start" fullWidth>
      <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start" fullWidth>
        <SDSText type="h4" text={describePlanKind(plan.kind)} dataTestId={DataTestIds.BILLING.SUMMARY_PLAN_KIND} />
        <SDSText
          type="body-large"
          text={describeInstallmentProgress(installmentsPaid, installmentCount)}
          color="text-secondary"
        />
        <SDSFlexbox direction="column" gap="text-small" fullWidth dataTestId={DataTestIds.BILLING.SUMMARY_PROGRESS}>
          <LinearProgressBar completed={installmentsPaid} total={installmentCount} />
        </SDSFlexbox>
        <DiscountLine plan={plan} />
      </SDSFlexbox>

      <SDSSeparator />

      {/* Three columns, 16px apart with rules between: the outer two share the
          spare width and the middle one keeps its own, as in the frame. */}
      <SDSFlexbox className="InstallmentFacts" direction="row" gap="card-large" alignItems="stretch" fullWidth>
        <SummaryFact
          className="SummaryFact--fill"
          heading="Total amount paid"
          value={formatBillingPlanAmount(plan.totalPaid)}
          supportingText={describePlanTotal(plan.planTotal)}
          dataTestId={DataTestIds.BILLING.SUMMARY_AMOUNT_PAID}
        />
        <FactRule />
        <SummaryFact
          heading="Remaining balance"
          value={plan.remainingBalance ? formatBillingPlanAmount(plan.remainingBalance) : '—'}
          supportingText={describeFinalPaymentDue(plan.finalPaymentDueAt ?? undefined)}
          dataTestId={DataTestIds.BILLING.SUMMARY_REMAINING_BALANCE}
        />
        <FactRule />
        <SummaryFact
          className="SummaryFact--fill"
          heading="Next payment"
          value={plan.nextPaymentAmount ? formatBillingPlanAmount(plan.nextPaymentAmount) : '—'}
          supportingText={describeNextPaymentDue(plan.nextPaymentDueAt ?? undefined)}
          dataTestId={DataTestIds.BILLING.SUMMARY_NEXT_PAYMENT}
        />
      </SDSFlexbox>

      <PackageActions refundSubject={{ productTitle: plan.productTitle, planLabel: describePlanKind(plan.kind) }}>
        <SDSButton
          text="View payment plan"
          mode="primary"
          size="medium"
          isSubmitting={isPortalLoading}
          onClick={onOpenPortal}
          dataTestId={DataTestIds.BILLING.VIEW_PAYMENT_PLAN}
        />
        {canPayRemainingBalance ? (
          <SDSButton
            text="Pay remaining balance"
            mode="outline"
            size="medium"
            isSubmitting={isPortalLoading}
            onClick={onOpenPortal}
            dataTestId={DataTestIds.BILLING.PAY_REMAINING_BALANCE}
          />
        ) : null}
      </PackageActions>
    </SDSFlexbox>
  );
}

/**
 * The plan's payment terms: what was agreed, what has been collected, and
 * what a member can still do about it.
 *
 * "View receipt" opens the provider's own receipt for the payment, resolved
 * when it is asked for. "View payment plan" and "Pay remaining balance" open
 * the Stripe portal instead: neither is a document, and the portal is the
 * thing that can actually collect a payment.
 */
export function PaymentSummary({
  plan,
  isPortalLoading,
  onOpenPortal,
  isReceiptLoading,
  onViewReceipt,
}: {
  plan: PaymentSummaryPlan;
  isPortalLoading: boolean;
  onOpenPortal(): void;
  isReceiptLoading: boolean;
  onViewReceipt(): void;
}) {
  return (
    <SDSFlexbox direction="column" fullWidth dataTestId={DataTestIds.BILLING.PAYMENT_SUMMARY}>
      {isInstallmentPlanKind(plan.kind) ? (
        <InstallmentSummary plan={plan} isPortalLoading={isPortalLoading} onOpenPortal={onOpenPortal} />
      ) : (
        <OneTimeSummary plan={plan} isReceiptLoading={isReceiptLoading} onViewReceipt={onViewReceipt} />
      )}
    </SDSFlexbox>
  );
}
